import { useOrders } from "@/hooks/useOrders";
import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import type { Account, Backtest } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { useFormatDate } from "@/hooks/useFormatDate";
import { Card, ExchangeMark } from "@/components/ui/kit";
import { OrderDetailsDialog } from "@/components/OrderDetailsDialog";
import { Pager } from "@/components/ui/Pager";
import { clampPage } from "@/lib/paging";
import {
  NO_FILTERS,
  exchangeOrderSymbols,
  filterExchangeOrders,
  type OrderDaysFilter,
  type OrderFilters,
} from "@/lib/exchange-orders";
import { realizedPnlByOrder } from "@/lib/order-trades";
import { approxQty, num, orderLabel, pnlColor, qtyOf, signedUsd } from "@/lib/order-format";

const PAGE = 200;
/** Rows per page in the table. */
const ROWS = 20;

const PERIODS: [OrderDaysFilter, string][] = [
  [0, "All time"],
  [1, "24h"],
  [7, "7 days"],
  [30, "30 days"],
];

const fieldLabel = "mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]";
const select = "!h-auto !min-h-0 w-full !py-1.5 !text-[13px]";
const th = "whitespace-nowrap border-b border-[var(--border)] px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]";
const td = "whitespace-nowrap border-b border-[var(--divider)] px-3 py-[9px]";

/**
 * Trades page: the orders that filled on the user's exchange accounts, with
 * the P&L of each one that closed a position. Filters sit in one row on top,
 * the matching orders below; a row opens the order's details.
 */
export function ExchangeOrders() {
  const api = useApi(["accounts", "backtests"]);
  const { dateTime, dateTimeShort } = useFormatDate();
  const accounts: Account[] = useMemo(() => api.accounts || [], [api.accounts]);
  const backtests: Backtest[] = useMemo(() => api.backtests || [], [api.backtests]);

  const [accountId, setAccountId] = useState("");
  const [filters, setFilters] = useState<OrderFilters>(NO_FILTERS);
  const [limit, setLimit] = useState(PAGE);
  const [now, setNow] = useState(() => Date.now());
  const [openId, setOpenId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const set = (patch: Partial<OrderFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const subscription = useOrders({ account_id: accountId, limit }, api.ready);
  const loading = subscription.status === 'loading' || subscription.status === 'idle';
  const error = subscription.error || '';
  const load = () => { subscription.refresh(); setNow(Date.now()); };
  const loaded = subscription.rows;
  const ofExchange = useMemo(() => (accountId ? loaded.filter((o) => o.account_id === accountId) : loaded), [loaded, accountId]);
  const symbols = useMemo(() => exchangeOrderSymbols(ofExchange), [ofExchange]);
  const rows = useMemo(() => filterExchangeOrders(ofExchange, filters, now), [ofExchange, filters, now]);
  // A new filter starts again from the first page.
  useEffect(() => setPage(0), [accountId, filters]);
  const safePage = clampPage(page, ROWS, rows.length);
  const pageRows = rows.slice(safePage * ROWS, (safePage + 1) * ROWS);
  const notFilled = useMemo(() => ofExchange.filter((o) => o.status !== "done").length, [ofExchange]);
  // Matched over everything loaded, so a filter can't hide the entry an exit closed.
  const pnlByOrder = useMemo(() => realizedPnlByOrder(loaded), [loaded]);

  // A symbol the chosen exchange has no orders for can't stay selected.
  useEffect(() => {
    if (filters.symbol && !loading && !symbols.some((s) => s.symbol === filters.symbol)) set({ symbol: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbols, loading]);

  const realized = useMemo(() => {
    let pnl = 0;
    let closes = 0;
    for (const o of rows) {
      const v = pnlByOrder.get(o.id);
      if (v == null) continue;
      pnl += v;
      closes++;
    }
    return { pnl, closes };
  }, [rows, pnlByOrder]);

  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const backtestById = useMemo(() => new Map(backtests.map((b) => [b.id, b])), [backtests]);
  /** Strategies that placed orders here, for the strategy filter. */
  const strategies = useMemo(() => {
    const ids = new Set(ofExchange.filter((o) => o.status === "done").map((o) => o.backtest_id).filter((id): id is string => !!id));
    return [...ids].map((id) => ({ id, name: backtestById.get(id)?.name || "Deleted strategy" })).sort((a, b) => a.name.localeCompare(b.name));
  }, [ofExchange, backtestById]);

  const anyFilter = Boolean(accountId || filters.symbol || filters.days || filters.backtestId);
  const clear = () => {
    setAccountId("");
    setLimit(PAGE);
    setFilters(NO_FILTERS);
  };
  const pickAccount = (id: string) => {
    setLimit(PAGE);
    setAccountId(id);
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="min-w-0">
            <span className={`block ${fieldLabel}`}>Exchange</span>
            <select className={select} value={accountId} onChange={(e) => pickAccount(e.target.value)} disabled={accounts.length === 0}>
              <option value="">All exchanges</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-0">
            <span className={`block ${fieldLabel}`}>Symbol</span>
            <select className={select} value={filters.symbol} onChange={(e) => set({ symbol: e.target.value })} disabled={symbols.length === 0}>
              <option value="">All symbols</option>
              {symbols.map((s) => (
                <option key={s.symbol} value={s.symbol}>
                  {s.symbol}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-0">
            <span className={`block ${fieldLabel}`}>Strategy</span>
            <select className={select} value={filters.backtestId} onChange={(e) => set({ backtestId: e.target.value })}>
              <option value="">All strategies</option>
              <option value="none">Manual (no strategy)</option>
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-0">
            <span className={`block ${fieldLabel}`}>Period</span>
            <select className={select} value={filters.days} onChange={(e) => set({ days: Number(e.target.value) as OrderDaysFilter })}>
              {PERIODS.map(([v, text]) => (
                <option key={v} value={v}>
                  {text}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[var(--border)] px-4 py-3">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px]" role="status">
            <b className="font-semibold text-[var(--text-primary)]">
              <span className="num">{rows.length.toLocaleString()}</span> {rows.length === 1 ? "order" : "orders"}
            </b>
            {realized.closes > 0 && (
              <span className="text-[var(--text-muted)]" title="Realized on the orders listed that closed a position, without fees and funding">
                P&amp;L{" "}
                <span className="num font-semibold" style={{ color: pnlColor(realized.pnl) }}>
                  {signedUsd(realized.pnl)}
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {anyFilter && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={clear}>
                Clear filters
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={() => void load()}>
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {error && <div className="m-3 rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{error}</div>}

        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-[13px] text-[var(--text-muted)]">
            {loading || !api.ready
              ? "Loading orders…"
              : anyFilter
                ? "No orders match these filters."
                : "No orders on your exchanges yet. They appear here when a live strategy places one."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  <th scope="col" className={th}>Time</th>
                  <th scope="col" className={th}>Symbol</th>
                  <th scope="col" className={th}>Action</th>
                  <th scope="col" className={`${th} text-right`}>Quantity</th>
                  <th scope="col" className={`${th} text-right`}>P&amp;L</th>
                  <th scope="col" className={th}>Strategy</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((o) => {
                  const account = accountById.get(o.account_id);
                  const bt = o.backtest_id ? backtestById.get(o.backtest_id) : undefined;
                  const pnl = pnlByOrder.get(o.id);
                  const buy = o.side === "BUY";
                  const label = orderLabel(o);
                  return (
                    <tr
                      key={o.id}
                      className="cursor-pointer hover:bg-[var(--bg-hover)]"
                      tabIndex={0}
                      aria-label={`Order details: ${label} ${o.symbol}`}
                      onClick={() => setOpenId(o.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setOpenId(o.id);
                        }
                      }}
                    >
                      <td className={`${td} num text-[var(--text-muted)]`} title={dateTime(o.created_at)}>
                        {dateTimeShort(o.created_at)}
                      </td>
                      <td className={`${td} font-medium text-[var(--text-primary)]`}>{o.symbol}</td>
                      <td className={td}>
                        <span className="inline-flex items-center gap-1.5 text-[var(--text-primary)]" title={buy ? "Buy" : "Sell"}>
                          {label}
                        </span>
                      </td>
                      <td className={`${td} num text-right`} title={num(qtyOf(o), 8)}>{approxQty(qtyOf(o))}</td>
                      <td className={`${td} num text-right`} style={{ color: pnlColor(pnl) }} title={pnl === undefined ? "Opened a position" : "Without fees and funding"}>
                        {pnl == null ? "—" : signedUsd(pnl)}
                      </td>
                      <td className={`${td} max-w-[280px]`}>
                        <div className={`truncate ${bt ? "text-[var(--text-primary)]" : "text-[var(--text-disabled)]"}`}>
                          {bt ? bt.name : o.backtest_id ? "Deleted strategy" : "Manual"}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                          {account && <ExchangeMark provider={account.provider} size={14} />}
                          {account?.name ?? "Removed account"}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager className="px-4 py-2.5" page={safePage} pageSize={ROWS} total={rows.length} onPage={setPage} />
          </div>
        )}

        {(loaded.length >= limit || notFilled > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-xs text-[var(--text-muted)]">
            <span>
              {loaded.length >= limit && `From the latest ${limit.toLocaleString()} orders. `}
              {notFilled > 0 &&
                `${notFilled.toLocaleString()} ${notFilled === 1 ? "order that didn't fill (rejected, canceled or still waiting) is" : "orders that didn't fill (rejected, canceled or still waiting) are"} not listed; ${notFilled === 1 ? "it is" : "they are"} on the strategy's page.`}
            </span>
            {loaded.length >= limit && (
              <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={() => setLimit((n) => n + PAGE)}>
                {loading ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        )}
      </Card>

      {openId && (() => {
        const o = loaded.find((r) => r.id === openId);
        if (!o) return null;
        return (
          <OrderDetailsDialog
            order={o}
            account={accountById.get(o.account_id)}
            strategy={o.backtest_id ? backtestById.get(o.backtest_id) : undefined}
            pnl={pnlByOrder.get(o.id)}
            onClose={() => setOpenId(null)}
          />
        );
      })()}
    </div>
  );
}
